<script>
// @ts-check

import BaseErrors from '@/components/base/BaseErrors.vue'
import BaseLabel from '@/components/base/BaseLabel.vue'
import { attrsMixin } from '@/core/mixins/attrs'
import { inputProps } from '@/core/props'
import { nextTick } from 'vue'

/**
 * Deprecated text input component
 * @deprecated Use VTextField instead
 */

export default {
  name: 'BaseInput',
  components: {
    BaseLabel,
    BaseErrors
  },
  mixins: [attrsMixin],
  props: {
    ...inputProps,
    /** @type {import('vue').Prop<any>} */
    modelValue: {
      default: null,
      validator: (value) => {
        return ['string', 'number'].includes(typeof value) || value === null
      }
    },
    modelModifiers: {
      type: Object,
      default: () => ({})
    },
    errors: {
      /** @type {import('vue').PropType<string[]|null>} */
      type: Array,
      default: () => []
    },
    type: {
      type: String,
      default: 'text'
    },
    required: {
      type: Boolean
    },
    loading: {
      type: Boolean
    },
    disabled: {
      type: Boolean
    },
    placeholder: {
      type: String,
      default: null
    },
    /**
     * Display an info icon with the given text in a tooltip next to the label.
     */
    info: {
      type: String,
      default: null
    },
    autocomplete: {
      type: String,
      default: 'off'
    },
    autofocus: {
      type: Boolean
    },
    isOcrValue: {
      type: Boolean
    },
    showErrorText: {
      type: Boolean,
      default: true
    }
  },
  emits: ['update:modelValue'],
  computed: {
    isLazy() {
      return !!this.modelModifiers.lazy
    },
    isDisabled() {
      return this.loading || this.disabled
    },
    isUppercase() {
      return !!this.modelModifiers.uppercase
    }
  },
  mounted() {
    if (this.autofocus !== false) {
      nextTick().then(() => {
        this.$refs.input.focus()
      })
    }
  },
  methods: {
    processAndEmit(event, shouldEmit) {
      let { value } = event.target
      if (this.isUppercase) {
        value = value.toUpperCase()
      }
      if (shouldEmit) {
        this.$emit('update:modelValue', value)
      }
      return null
    },
    handleInput(event) {
      this.processAndEmit(event, !this.isLazy)
    },
    handleChange(event) {
      this.processAndEmit(event, this.isLazy)
    }
  }
}
</script>

<template>
  <div class="w-full" :class="classes" :style="styles">
    <BaseLabel :for="id" :info :required>
      {{ label }}
    </BaseLabel>
    <span class="spinner-wrapper" :class="{ loading }">
      <input
        v-bind="attributes"
        :id
        ref="input"
        class="text-input"
        :class="[
          {
            'border border-green bg-green-50': isOcrValue,
            'has-error': errors?.length,
            'pr-12': loading
          }
        ]"
        :value="modelValue"
        :disabled="isDisabled"
        :autocomplete
        :placeholder
        :type
        @input="handleInput"
        @change="handleChange"
      />
      <div
        v-if="!!$slots.suffix"
        class="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3"
      >
        <slot name="suffix" />
      </div>
    </span>
    <BaseErrors v-if="showErrorText" :label :errors />
    <slot />
  </div>
</template>

<style lang="scss" scoped>
@reference '../../styles/main.css';

.spinner-wrapper {
  @apply block relative;

  &.loading::after {
    display: block;
    content: '';
    height: 1rem;
    width: 1rem;
    position: absolute;
    top: calc(50% - (1rem / 2));
    right: 1rem;
    border: 2px solid var(--color-primary);
    border-radius: 9999px;
    border-color: transparent transparent var(--color-primary)
      var(--color-primary);
    animation: donut-spin 0.9s infinite linear;
  }
}
</style>
